import type { ReactNode } from 'react';
import { DefinitionTooltip, MenuButton } from '@carbon/react';

interface ExportMenuButtonProps {
  /** One `MenuItem` per export the surface offers (e.g. "Spatial Data", "Excel"). */
  children: ReactNode;
  /**
   * Disables the whole trigger — set only when no offered export is currently available (e.g. the
   * filtered set is empty). Individual items disable themselves when just their own export is
   * unavailable.
   */
  disabled?: boolean;
  /**
   * Why the whole trigger is disabled, shown in a tooltip (e.g. "No areas match the current
   * filters"). Only meaningful alongside `disabled`; the button is then wrapped in a
   * DefinitionTooltip, mirroring the single-button export triggers it replaces.
   */
  disabledReason?: string;
}

/**
 * The shared "Export" dropdown trigger for the catalog surfaces (Areas, Type Curves). A Carbon
 * {@link MenuButton} labelled "Export" that reveals one `MenuItem` per export the surface offers
 * (passed as `children`) — e.g. "Spatial Data" and "Excel". Kept deliberately thin so every surface's
 * dropdown looks and anchors identically; each surface owns its items, their enabled state, and the
 * flows (dialogs / banners) those items drive as siblings of this button.
 *
 * @param {ExportMenuButtonProps} props - The menu items, plus an optional whole-button disabled reason.
 * @return {JSX.Element} The "Export" menu button, tooltip-wrapped when fully disabled.
 */
export function ExportMenuButton({
  children,
  disabled,
  disabledReason,
}: ExportMenuButtonProps) {
  const menu = (
    <MenuButton
      label="Export"
      kind="tertiary"
      size="md"
      // Anchored to the button's right edge — it sits at the right end of the page header, so the
      // menu expands leftward and never clips off-screen. Mirrors the eval3 Actions menu.
      menuAlignment="bottom-end"
      disabled={disabled}
    >
      {children}
    </MenuButton>
  );

  if (disabled && disabledReason) {
    return (
      <DefinitionTooltip align="bottom-end" definition={disabledReason}>
        {menu}
      </DefinitionTooltip>
    );
  }

  return menu;
}
